import React, { useState, useEffect, useContext } from 'react';
import { 
  Box, 
  Typography, 
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress
} from '@mui/material';
import { ReportContext } from '../../Context';
import axios from 'axios';
import DescriptionIcon from '@mui/icons-material/Description';
import { Document, Packer, Paragraph, TextRun, AlignmentType, Header, BorderStyle, Table, TableRow, TableCell, WidthType, ImageRun } from 'docx';
import { saveAs } from 'file-saver';

function NonReleaseBody() {
  const { report } = useContext(ReportContext);
  
  const [loading, setLoading] = useState(true);
  const [intervenants, setIntervenants] = useState([]);
  const [personnesPhysiques, setPersonnesPhysiques] = useState([]);
  const [hopitaux, setHopitaux] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [settings, setSettings] = useState(null);
  
  const [policierSignataire, setPolicierSignataire] = useState('');
  const [policierSignataireData, setPolicierSignataireData] = useState(null);
  const [personneHospitalisee, setPersonneHospitalisee] = useState('');
  const [personneHospitaliseeData, setPersonneHospitaliseeData] = useState(null);
  const [hopitalConcerne, setHopitalConcerne] = useState('');
  const [hopitalConcerneData, setHopitalConcerneData] = useState(null);
  const [dateAvis, setDateAvis] = useState('');

  // Helpers logo (copié de Download.js, simplifié)
  const svgTextToPngBase64 = (svgText, width = 150, height = 150) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        const base64 = canvas.toDataURL('image/png').split(',')[1];
        resolve(base64);
      };
      
      img.onerror = reject;
      
      const svgBlob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);
      img.src = url;
    });
  };

  const base64ToUint8Array = (base64) => {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  };

  const loadLogoPng = async () => {
    const svgContent = `<?xml version="1.0" encoding="utf-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 150 150" width="150" height="150">
  <circle cx="75" cy="75" r="74" fill="#FFFFFF" stroke="#244BA6" stroke-width="2"/>
  <path fill="#244BA6" d="M150,75c0-41.4-33.6-75-75-75S0,33.6,0,75c0,39.5,30.5,71.9,69.3,74.8c-3.3-4-7.4-9.5-11-14.6
    c-6.1-8.6-13.4-21.5-13.7-34.4c-0.2-9.6,2-14.8,14.6-34.7c4.9-7.8,8.2-21.1,7.7-23.9c-0.5-2.9,4.6,4.3,6.5,9.1
    c2.5,6.4,2.2,11.2-2.5,24.4c-2.2,6.3-5.9,16.4-2.2,20.9c-0.4-0.5,11.9-13.2,14.8-26.2c2.2-10,1.3-23.3-3-33.1
    c-3.8-8.6-8.3-15.7-7.4-27.1c0.4-4.8,1.7-9.4,1.8-9.7c2.8,7,4,9.5,5.1,11.5c5.1,9.6,15.6,23.7,20.5,33.4
    c5,9.6,8.8,25.5,3,41.7c-5.7,15.9-11.1,21.3-16.9,34.2c-4.3,9.6-7.9,21.4-9.7,28.8C117.5,149,150,115.9,150,75"/>
</svg>`;
    
    try {
      const base64 = await svgTextToPngBase64(svgContent, 100, 100);
      return base64ToUint8Array(base64);
    } catch (error) {
      console.warn('Erreur conversion logo:', error);
      return null;
    }
  };
  
  // Fonction pour construire le numéro de PV complet
  const buildReportNumber = () => {
    if (!reportData || !settings) return 'XXXXXX';
    
    const shortJudicialDistrict = settings.shortJudicialDistrict || '__';
    const indice = reportData.indice || '__';
    const judicialDivision = settings.judicialDivision || '__';
    const number = report?.number || '__';
    const year = report?.year || '__';
    
    return `${shortJudicialDistrict}.${indice}.${judicialDivision}.${number}/${year}`;
  };
  
  // Fonction pour convertir les grades en texte complet
  const convertGrade = (gradeCode) => {
    const gradeMapping = {
      'AAGP': 'Aspirant Agent de Police',
      'AGP': 'Agent de Police',
      '1AGP': 'Premier Agent de Police',
      'ADAGP': 'Aspirant Agent de sécurisation de Police',
      'DAGP': 'Agent de sécurisation de Police',
      '1DAGP': 'Premier Agent de sécurisation de Police',
      'ABASP': 'Aspirant Assistant de Sécurisation de Police',
      'BASP': 'Assistant de Sécurisation de Police',
      '1BASP': 'Premier Assistant de Sécurisation de Police',
      'BCSP': 'Coordonnateur de Sécurisation de Police',
      '1BCSP': 'Premier Coordonnateur de Sécurisation de Police',
      'AINP': 'Aspirant Inspecteur de Police',
      'INP': 'Inspecteur de Police',
      '1INP': 'Premier Inspecteur de Police',
      'AINPP': 'Aspirant Inspecteur Principal de Police',
      'INPP': 'Inspecteur Principal de Police',
      '1INPP': 'Premier Inspecteur Principal de Police',
      'ACP': 'Aspirant Commissaire de Police',
      'CP': 'Commissaire de Police',
      '1CP': 'Premier Commissaire de Police',
      'CDP': 'Commissaire Divisionnaire de Police',
      '1CDP': 'Premier Commissaire Divisionnaire de Police'
    };
    return gradeMapping[gradeCode] || gradeCode;
  };
  
  // Charger les données au montage du composant
  useEffect(() => {
    const fetchData = async () => {
      if (!report || !report.number || !report.year) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const token = sessionStorage.getItem('authToken');
        
        // Récupérer les paramètres
        const settingsResponse = await axios.get('/api/settings');
        setSettings(settingsResponse.data);
        
        // Récupérer les données du rapport
        const reportResponse = await axios.get(
          `/api/report/search?number=${report.number}&year=${report.year}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        setReportData(reportResponse.data);
        
        // Récupérer TOUS les utilisateurs
        const allUsersResponse = await axios.get(
          '/api/user',
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const reportIntervenants = reportResponse.data.intervenants || [];
        const allUsers = allUsersResponse.data || [];
        
        // 1. Les intervenants du rapport en haut (avec leur rank déjà converti)
        const intervenantsOptions = reportIntervenants.map(i => ({
          value: `${i.rank} ${i.lastname.toUpperCase()} ${i.firstname}`,
          data: i,
          isReportIntervenant: true
        })).sort((a, b) => a.value.localeCompare(b.value, 'fr', { sensitivity: 'accent' }));
        
        const allOptions = [...intervenantsOptions];
        
        // 2. Ajouter un séparateur
        if (allUsers.length > 0) {
          allOptions.push({
            value: '--- Autres collègues ---',
            data: null,
            isSeparator: true
          });
          
          // 3. Filtrer les utilisateurs qui ne sont pas déjà dans les intervenants du rapport
          const reportIntervenantIds = reportIntervenants.map(i => i.userId).filter(Boolean);
          const otherUsers = allUsers.filter(user => !reportIntervenantIds.includes(user._id));
          
          // 4. Ajouter les autres utilisateurs avec conversion du grade
          const usersOptions = otherUsers.map(user => {
            const grade = convertGrade(user.rank || '');
            const displayText = `${grade} ${user.lastname.toUpperCase()} ${user.firstname}`;
            return {
              value: displayText,
              data: {
                isExternal: false,
                sign: false,
                rank: grade,
                lastname: user.lastname,
                firstname: user.firstname,
                userId: user._id
              },
              isOtherUser: true
            };
          }).sort((a, b) => a.value.localeCompare(b.value, 'fr', { sensitivity: 'accent' }));
          
          allOptions.push(...usersOptions);
        }
        
        setIntervenants(allOptions);
        
        // Formater les personnes physiques (uniquement celles avec un nom)
        const personnesFormatted = reportResponse.data.personnesPhysiques
          .filter(p => p.lastname && !p.isUnknown)
          .map(p => ({
            value: `${p.lastname.toUpperCase()} ${p.firstname || ''}`,
            data: p
          }));
        
        setPersonnesPhysiques(personnesFormatted);
        
        // Récupérer la liste des hôpitaux
        try {
          const hopitauxResponse = await axios.get(
            '/api/hospitals',
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          console.log('Réponse hôpitaux:', hopitauxResponse.data);
          
          if (Array.isArray(hopitauxResponse.data)) {
            setHopitaux(hopitauxResponse.data);
          } else {
            console.error('Format de réponse inattendu pour les hôpitaux:', hopitauxResponse.data);
            setHopitaux([]);
          }
        } catch (hopitauxError) {
          console.error('Erreur lors du chargement des hôpitaux:', hopitauxError);
          setHopitaux([]);
        }
        
        // Pré-remplir la date avec aujourd'hui (heure locale)
        const now = new Date();
        const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        setDateAvis(today);
        
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
        setLoading(false);
      }
    };
    
    fetchData();
  }, [report]);
  
  // Gérer la sélection du policier signataire
  const handlePolicierChange = (e) => {
    const value = e.target.value;
    setPolicierSignataire(value);
    const selectedIntervenant = intervenants.find(i => i.value === value && !i.isSeparator);
    setPolicierSignataireData(selectedIntervenant?.data || null);
  };
  
  // Gérer la sélection de la personne hospitalisée
  const handlePersonneChange = (e) => {
    const value = e.target.value;
    setPersonneHospitalisee(value);
    const selectedPersonne = personnesPhysiques.find(p => p.value === value);
    setPersonneHospitaliseeData(selectedPersonne?.data || null);
  };
  
  // Gérer la sélection de l'hôpital
  const handleHopitalChange = (e) => {
    const value = e.target.value;
    setHopitalConcerne(value);
    const selectedHopital = hopitaux.find(h => h.nom === value);
    setHopitalConcerneData(selectedHopital || null);
  };
  
  // Vérifier si tous les champs sont remplis
  const isFormComplete = policierSignataire && personneHospitalisee && hopitalConcerne && dateAvis;
  
  // Fonction pour formater la date en français
  const formatDateFrench = (dateString) => {
    const date = new Date(dateString);
    const jours = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
    const mois = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
    
    const jour = jours[date.getDay()];
    const numJour = String(date.getDate()).padStart(2, '0');
    const nomMois = mois[date.getMonth()];
    const annee = date.getFullYear();
    
    return `${jour} ${numJour} ${nomMois} ${annee}`;
  };
  
  // Fonction pour formater la date en JJ/MM/AAAA
  const formatDateShort = (dateString) => {
    const date = new Date(dateString);
    const jour = String(date.getDate()).padStart(2, '0');
    const mois = String(date.getMonth() + 1).padStart(2, '0');
    const annee = date.getFullYear();
    
    return `${jour}/${mois}/${annee}`;
  };
  
  const handleGenerate = async () => {
    if (!isFormComplete || !reportData || !settings || !personneHospitaliseeData || !hopitalConcerneData || !policierSignataireData) {
      console.error('Données manquantes pour générer le document');
      return;
    }
    
    try {
      const reportNumber = buildReportNumber();
      const dateLongue = formatDateFrench(dateAvis);
      const dateCourte = formatDateShort(dateAvis);
      const logoImageData = await loadLogoPng();
      
      const fontFamily = 'Helvetica';
      const fontSize9pt = 18;
      const fontSize10pt = 20;
      const typeValue = settings.type === 'local' ? 'Locale' : settings.type === 'federal' ? 'Fédérale' : settings.type || 'N/A';
      
      const buildHeader = () => {
        const noBorders = {
          top: { style: BorderStyle.NONE },
          bottom: { style: BorderStyle.NONE },
          left: { style: BorderStyle.NONE },
          right: { style: BorderStyle.NONE },
        };
        
        const headerRows = [
          new TableRow({
            children: [
              new TableCell({
                width: { size: 12, type: WidthType.PERCENTAGE },
                borders: noBorders,
                children: [
                  new Paragraph({
                    children: logoImageData ? [
                      new ImageRun({
                        data: logoImageData,
                        transformation: { width: 40, height: 40 },
                        type: 'png',
                      })
                    ] : []
                  })
                ]
              }),
              new TableCell({
                width: { size: 38, type: WidthType.PERCENTAGE },
                borders: noBorders,
                children: [
                  new Paragraph({
                    spacing: { after: 40 },
                    children: [
                      new TextRun({
                        text: `Police ${typeValue}`,
                        font: fontFamily,
                        size: fontSize9pt,
                      }),
                    ],
                  }),
                  new Paragraph({
                    spacing: { after: 40 },
                    children: [
                      new TextRun({
                        text: `${settings.clientName || 'N/A'} - ${settings.zoneNumber || 'N/A'}`,
                        font: fontFamily,
                        size: fontSize9pt,
                        bold: true,
                      }),
                    ],
                  }),
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: `Service ${reportData.unit || ''}`,
                        font: fontFamily,
                        size: fontSize9pt,
                      }),
                    ],
                  }),
                ],
              }),
              new TableCell({
                width: { size: 50, type: WidthType.PERCENTAGE },
                borders: noBorders,
                children: [
                  new Paragraph({
                    alignment: AlignmentType.RIGHT,
                    spacing: { after: 40 },
                    children: [
                      new TextRun({
                        text: `Procès-verbal ${reportData.reportType === "subsequent" ? "subséquent" : reportData.reportType}`,
                        font: fontFamily,
                        size: fontSize9pt,
                      }),
                    ],
                  }),
                  new Paragraph({
                    alignment: AlignmentType.RIGHT,
                    spacing: { after: 40 },
                    children: [
                      new TextRun({
                        text: reportNumber,
                        font: fontFamily,
                        size: fontSize9pt,
                        bold: true,
                      }),
                    ],
                  }),
                  new Paragraph({
                    alignment: AlignmentType.RIGHT,
                    children: [
                      new TextRun({
                        text: 'Annexe n° _____',
                        font: fontFamily,
                        size: fontSize9pt
                      }),
                    ],
                  }),
                ],
              }),
            ],
          }),
        ];
        
        const headerChildren = [
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            borders: {
              top: { style: BorderStyle.NONE },
              bottom: { style: BorderStyle.NONE },
              left: { style: BorderStyle.NONE },
              right: { style: BorderStyle.NONE },
              insideHorizontal: { style: BorderStyle.NONE },
              insideVertical: { style: BorderStyle.NONE },
            },
            rows: headerRows,
          }),
          new Paragraph({
            border: {
              bottom: {
                color: '000000',
                space: 1,
                style: BorderStyle.SINGLE,
                size: 6,
              },
            },
            spacing: { after: 200, before: 0, line: 20 },
            children: [
              new TextRun({
                text: '',
                size: 4,
              }),
            ],
          })
        ];
        
        return new Header({ children: headerChildren });
      };
      
      const sexeFr = personneHospitaliseeData.gender === 'M' ? 'Masculin' : 
                     personneHospitaliseeData.gender === 'F' ? 'Féminin' : 
                     personneHospitaliseeData.gender || '';
      
      const dateNaissance = personneHospitaliseeData.birthdate ? 
        formatDateShort(personneHospitaliseeData.birthdate) : '';
      
      const birthLine = dateNaissance ? `${dateNaissance}${personneHospitaliseeData.birthPlace ? ` à ${personneHospitaliseeData.birthPlace}` : ''}` : '';
      
      const buildNationalityParagraph = () => {
        const nat = personneHospitaliseeData.nationality;
        const rrn = personneHospitaliseeData.nationalNumber;
        
        if (!nat && !rrn) return null;
        
        // Nationalité seule
        if (nat && !rrn) {
          return infoParagraph('Nationalité : ', nat, { bold: false });
        }
        
        // Nationalité + RRN (numéro en gras uniquement)
        if (nat && rrn) {
          return new Paragraph({
            children: [
              new TextRun({ text: 'Nationalité : ', font: fontFamily, size: fontSize10pt }),
              new TextRun({ text: nat, font: fontFamily, size: fontSize10pt }),
              new TextRun({ text: ' (RRN : ', font: fontFamily, size: fontSize10pt }),
              new TextRun({ text: rrn, font: fontFamily, size: fontSize10pt, bold: true }),
              new TextRun({ text: ')', font: fontFamily, size: fontSize10pt }),
            ]
          });
        }
        
        // RRN seul (label non gras, numéro gras)
        return new Paragraph({
          children: [
            new TextRun({ text: 'RRN : ', font: fontFamily, size: fontSize10pt }),
            new TextRun({ text: rrn, font: fontFamily, size: fontSize10pt, bold: true }),
          ]
        });
      };
      
      const infoParagraph = (label, value, options = {}) => {
        const isBold = options.bold !== undefined ? options.bold : true;
        return value ? new Paragraph({
          children: [
            new TextRun({ text: `${label}`, font: fontFamily, size: fontSize10pt }),
            new TextRun({ 
              text: value, 
              font: fontFamily, 
              size: fontSize10pt, 
              bold: isBold,
              underline: options.underline ? {} : undefined
            })
          ]
        }) : null;
      };
      
      const identityParagraphs = [
        personneHospitaliseeData ? new Paragraph({
          children: [
            new TextRun({ text: 'Nom et prénom : ', font: fontFamily, size: fontSize10pt }),
            new TextRun({ 
              text: (personneHospitaliseeData.lastname || '').toUpperCase(), 
              font: fontFamily, 
              size: fontSize10pt, 
              bold: true,
              underline: {}
            }),
            new TextRun({ text: ' ', font: fontFamily, size: fontSize10pt }),
            new TextRun({ 
              text: personneHospitaliseeData.firstname || '', 
              font: fontFamily, 
              size: fontSize10pt, 
              bold: true
            }),
            personneHospitaliseeData.otherFirstnames ? new TextRun({ 
              text: ` ${personneHospitaliseeData.otherFirstnames}`, 
              font: fontFamily, 
              size: fontSize10pt 
            }) : null
          ].filter(Boolean)
        }) : null,
        infoParagraph('Date et lieu de naissance : ', birthLine),
        infoParagraph('Sexe : ', sexeFr),
        infoParagraph('État civil : ', personneHospitaliseeData.civilStatus || ''),
        buildNationalityParagraph(),
        infoParagraph('Profession : ', personneHospitaliseeData.profession || ''),
        infoParagraph('Domicile : ', personneHospitaliseeData.residence || ''),
      ].filter(Boolean);
      
      // Créer le document
      const doc = new Document({
        sections: [{
          properties: {},
          headers: {
            default: buildHeader()
          },
          children: [
            // Titre dans un cadre
            new Table({
              width: {
                size: 100,
                type: WidthType.PERCENTAGE
              },
              borders: {
                top: { style: BorderStyle.SINGLE, size: 1, color: '000000' },
                bottom: { style: BorderStyle.SINGLE, size: 1, color: '000000' },
                left: { style: BorderStyle.SINGLE, size: 1, color: '000000' },
                right: { style: BorderStyle.SINGLE, size: 1, color: '000000' }
              },
              rows: [
                new TableRow({
                  children: [
                    new TableCell({
                      children: [
                        new Paragraph({
                          alignment: AlignmentType.CENTER,
                          children: [
                            new TextRun({
                              text: 'AVIS DE NON DISPOSITION DU CORPS',
                              font: 'Helvetica',
                              size: 28,
                              bold: true
                            })
                          ]
                        })
                      ]
                    })
                  ]
                })
              ]
            }),
            
            // Espace
            new Paragraph({ text: '' }),
            
            // Corps du texte (deux premiers paragraphes fusionnés)
            new Paragraph({
              children: [
                new TextRun({
                  text: `Avis à la direction de ${hopitalConcerneData.nom} sis à ${hopitalConcerneData.adresse}, les circonstances qui ont amené l\'admission dans votre établissement de la personne dont l\'identité figure ci-dessous font l\'objet d\'une enquête judiciaire.`,
                  font: 'Helvetica',
                  size: fontSize10pt
                })
              ]
            }),
            
            new Paragraph({ text: '' }),
            
            ...identityParagraphs,
            
            new Paragraph({ text: '' }),
            
            new Paragraph({
              children: [
                new TextRun({
                  text: 'En cas d\'issue fatale, il est interdit de disposer du corps, des vêtements, et des éventuels objets l\'accompagnant sans l\'autorisation préalable du Parquet saisi.',
                  font: 'Helvetica',
                  size: fontSize10pt
                })
              ]
            }),
            
            new Paragraph({ text: '' }),
            
            new Paragraph({
              children: [
                new TextRun({
                  text: 'De plus, le corps ne peut être montré à aucune personne, sans autorisation du Magistrat.',
                  font: 'Helvetica',
                  size: fontSize10pt
                })
              ]
            }),
            
            new Paragraph({ text: '' }),
            
            new Paragraph({
              children: [
                new TextRun({
                  text: 'Cet avis doit accompagner l\'individu lors de son transfert vers un autre établissement.',
                  font: 'Helvetica',
                  size: fontSize10pt,
                  bold: true
                })
              ]
            }),
            
            new Paragraph({ text: '' }),
            
            new Paragraph({
              children: [
                new TextRun({
                  text: `Reçu le ${dateLongue} du service ${reportData.unit || ''} de la ${settings.zoneNamePV} (${settings.zoneNumber}) sis à ${settings.zoneAddress}, sous l'arrondissement judiciaire de ${settings.judicialDistrict}.`,
                  font: 'Helvetica',
                  size: fontSize10pt
                })
              ]
            }),
            
            new Paragraph({ text: '' }),
            
            new Paragraph({
              children: [
                new TextRun({
                  text: 'Notice du procès-verbal : ',
                  font: 'Helvetica',
                  size: fontSize10pt
                }),
                new TextRun({
                  text: reportNumber,
                  font: 'Helvetica',
                  size: fontSize10pt,
                  bold: true
                })
              ]
            }),
            
            new Paragraph({ text: '' }),
            new Paragraph({ text: '' }),
            
            // Table pour les signatures
            new Table({
              width: {
                size: 100,
                type: WidthType.PERCENTAGE
              },
              borders: {
                top: { style: BorderStyle.NONE },
                bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE },
                right: { style: BorderStyle.NONE },
                insideHorizontal: { style: BorderStyle.NONE },
                insideVertical: { style: BorderStyle.NONE }
              },
              rows: [
                new TableRow({
                  children: [
                    new TableCell({
                      borders: {
                        top: { style: BorderStyle.NONE },
                        bottom: { style: BorderStyle.NONE },
                        left: { style: BorderStyle.NONE },
                        right: { style: BorderStyle.NONE }
                      },
                      width: {
                        size: 50,
                        type: WidthType.PERCENTAGE
                      },
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: `Pour prise de connaissance`,
                              font: 'Helvetica',
                          size: fontSize10pt
                            })
                          ]
                        }),
                        new Paragraph({
                            children: [
                              new TextRun({
                                text: `le ${dateCourte},`,
                                font: 'Helvetica',
                            size: fontSize10pt
                              })
                            ]
                          }),
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: 'Nom, qualité et signature',
                              font: 'Helvetica',
                          size: fontSize10pt,
                              italics: true
                            })
                          ]
                        })
                      ]
                    }),
                    new TableCell({
                      borders: {
                        top: { style: BorderStyle.NONE },
                        bottom: { style: BorderStyle.NONE },
                        left: { style: BorderStyle.NONE },
                        right: { style: BorderStyle.NONE }
                      },
                      width: {
                        size: 50,
                        type: WidthType.PERCENTAGE
                      },
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: 'Signature de',
                              font: 'Helvetica',
                          size: fontSize10pt
                            })
                          ]
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: policierSignataireData.rank,
                              font: 'Helvetica',
                          size: fontSize10pt
                            })
                          ]
                        }),
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: `${policierSignataireData.lastname.toUpperCase()} ${policierSignataireData.firstname}`,
                              font: 'Helvetica',
                          size: fontSize10pt,
                              bold: true
                            })
                          ]
                        })
                      ]
                    })
                  ]
                })
              ]
            })
          ]
        }]
      });
      
      // Générer et télécharger le fichier
      const blob = await Packer.toBlob(doc);
      const fileName = `${reportNumber}-avis-non-dispo.docx`;
      saveAs(blob, fileName);

      // Log du téléchargement
      try {
        const token = sessionStorage.getItem('authToken');
        
        await axios.post('/api/appendices/log-download', {
          appendixType: 'NonReleaseBody',
          appendixTitle: 'Avis de non disposition du corps',
          reportNumber: `${report?.number}/${report?.year}`,
          formData: {
            policierSignataire: `${policierSignataireData.rank} ${policierSignataireData.lastname?.toUpperCase()} ${policierSignataireData.firstname}`,
            personneHospitalisee: `${personneHospitaliseeData.lastname?.toUpperCase()} ${personneHospitaliseeData.firstname}`,
            hopital: hopitalConcerneData.nom,
            adresseHopital: hopitalConcerneData.adresse,
            dateAvis: dateAvis
          }
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (logError) {
        console.warn('Erreur lors du log du téléchargement:', logError);
        // Ne pas bloquer le téléchargement en cas d'erreur de log
      }
      
    } catch (error) {
      console.error('Erreur lors de la génération du document:', error);
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 0 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Complétez les informations ci-dessous pour générer l'avis de non disposition du corps.
        </Typography>
        
        <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Policier signataire */}
          <FormControl fullWidth required>
            <InputLabel id="policier-signataire-label">Policier signataire</InputLabel>
            <Select
              labelId="policier-signataire-label"
              value={policierSignataire}
              onChange={handlePolicierChange}
              label="Policier signataire"
            >
              {intervenants.map((intervenant, index) => {
                // Vérifier si c'est un séparateur
                const isSeparator = intervenant.isSeparator;
                
                return (
                  <MenuItem 
                    key={index} 
                    value={intervenant.value}
                    disabled={isSeparator}
                    sx={isSeparator ? { 
                      fontWeight: 'bold', 
                      backgroundColor: '#f5f5f5',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      cursor: 'default'
                    } : {}}
                  >
                    {intervenant.value}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          
          {/* Personne hospitalisée */}
          <FormControl fullWidth required>
            <InputLabel id="personne-hospitalisee-label">Personne hospitalisée</InputLabel>
            <Select
              labelId="personne-hospitalisee-label"
              value={personneHospitalisee}
              onChange={handlePersonneChange}
              label="Personne hospitalisée"
            >
              {personnesPhysiques.map((personne, index) => (
                <MenuItem key={index} value={personne.value}>
                  {personne.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {/* Hôpital concerné */}
          <FormControl fullWidth required>
            <InputLabel id="hopital-concerne-label">Hôpital concerné</InputLabel>
            <Select
              labelId="hopital-concerne-label"
              value={hopitalConcerne}
              onChange={handleHopitalChange}
              label="Hôpital concerné"
            >
              {hopitaux.map((hopital) => (
                <MenuItem key={hopital.id} value={hopital.nom}>
                  {hopital.nom}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {/* Date de l'avis */}
          <TextField
            fullWidth
            required
            label="Date de l'avis"
            type="date"
            value={dateAvis}
            onChange={(e) => setDateAvis(e.target.value)}
            InputLabelProps={{
              shrink: true
            }}
          />
          
          {/* Bouton de génération */}
          {isFormComplete && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<DescriptionIcon />}
                onClick={handleGenerate}
                sx={{
                  minWidth: 250,
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1rem'
                }}
              >
                Générer le document
              </Button>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}

export default NonReleaseBody;

